<template>
    <div class="pb-5 pt-5 instance">
        <div>
            <div v-if="error" class="alert alert-danger">
                {{ error }}
            </div>
            <div v-else>
                <div class="row m-0">
                    <div class="col-12">
                        <div class="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 pt-2 mt-1 mb-1">
                            <h5 class="title mt-2">Configuration</h5>
                        </div>
                        <div class="widget-actions">
                            <div v-for="action in widgetActions" :key="action.label" class="widget-action">
                                <component :is="action.url ? 'a' : 'button'" :href="action.url || undefined"
                                    :disabled="!action.url" target="_blank" rel="noopener noreferrer"
                                    class="btn btn-sm btn-success btn-circle" :data-testid="action.testId">
                                    <i :class="`fa ${action.icon} me-2`" aria-hidden="true"></i>{{ action.label }}
                                </component>
                                <p>{{ action.description }}</p>
                                <p v-if="action.message" class="widget-action__error" role="status">{{ action.message }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="formErrors" ref="formErrorSummary" class="qr-settings-errors" role="alert" tabindex="-1">
                    <p v-for="(msg, idx) in formErrors" :key="idx">{{ msg }}</p>
                </div>

                <LoaderComponent v-if="isFormLoading" />

                <form v-else class="ordering-configuration-form" @submit.prevent="saveSettings" method="post">
                    <OrderingTableSettingsSection title="Présentation du widget" icon="image"
                        description="Personnalise ce que les clients découvrent en ouvrant la carte."
                        :disabled="!has_right_to_update">
                        <div class="configuration-presentation">
                            <div>
                                <p class="form-label fw-semibold">Image de l’établissement</p>
                                <image-uploader
                                    :public-image-uri="widgetImagePublicUri"
                                    :image-path="null"
                                    :upload-uri="''"
                                    :delete-uri="null"
                                    :standalone="false"
                                    :disabled="!has_right_to_update"
                                    @file-selected="onWidgetImageSelected"
                                    allowedExtensions=".png,.jpg,.jpeg,.JPG,.webp"
                                    :maxSize="10" />
                            </div>
                            <div>
                                <label class="form-label fw-semibold" for="restaurant-description">Description de l’établissement</label>
                                <textarea
                                    id="restaurant-description"
                                    v-model="description"
                                    rows="5"
                                    placeholder="Décris ton établissement…"
                                    class="form-control"
                                    :disabled="!has_right_to_update"></textarea>
                            </div>
                        </div>
                    </OrderingTableSettingsSection>

                    <OrderingTableSettingsSection title="Informations client" icon="users"
                        description="Choisis les informations à demander au moment de la commande."
                        :disabled="!has_right_to_update">
                        <div class="configuration-setting">
                            <div>
                                <label for="ordering-customer-name" class="fw-semibold mb-1">Afficher le champ nom</label>
                                <p id="ordering-customer-name-help" class="small text-muted mb-0">Permet aux clients de saisir leur nom.</p>
                            </div>
                            <ToggleInput v-model="collectCustomerName" input-id="ordering-customer-name"
                                described-by="ordering-customer-name-help" :disabled="!has_right_to_update" />
                        </div>
                        <div class="configuration-setting">
                            <div>
                                <label for="ordering-beeper-number" class="fw-semibold mb-1">Demander le numéro de beeper / chevalet</label>
                                <p id="ordering-beeper-number-help" class="small text-muted mb-0">Rend ce numéro obligatoire à la validation du panier.</p>
                            </div>
                            <ToggleInput v-model="collectBeeperNumber" input-id="ordering-beeper-number"
                                described-by="ordering-beeper-number-help" :disabled="!has_right_to_update" />
                        </div>
                        <div class="configuration-setting">
                            <div>
                                <label for="ordering-customer-comment" class="fw-semibold mb-1">Afficher le champ commentaire</label>
                                <p id="ordering-customer-comment-help" class="small text-muted mb-0">Permet aux clients d’ajouter un commentaire.</p>
                            </div>
                            <ToggleInput v-model="collectCustomerComment" input-id="ordering-customer-comment"
                                described-by="ordering-customer-comment-help" :disabled="!has_right_to_update" />
                        </div>
                    </OrderingTableSettingsSection>

                    <OrderingTableScanSettings v-model="scanSettings" :opening-hours="orderingHours" :timezone="orderingHoursTimezone"
                        :errors="scanErrors" :disabled="!has_right_to_update" @hours-change="orderingHours = $event" />

                    <div class="configuration-actions">
                        <button
                            :disabled="!has_right_to_update"
                            type="button"
                            name="submit"
                            class="btn btn-success btn-circle"
                            @click="saveSettings">
                            Enregistrer
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import LoaderComponent from "../../../components/LoaderComponent.vue";
import ImageUploader from "../../../components/forms/ImageUploaderComponent.vue";
import ToggleInput from "../../../components/forms/ToggleInput.vue";
import OrderingTableScanSettings from "./components/OrderingTableScanSettings.vue";
import OrderingTableSettingsSection from "./components/OrderingTableSettingsSection.vue";

export default {
    components: {
        LoaderComponent,
        ImageUploader,
        ToggleInput,
        OrderingTableScanSettings,
        OrderingTableSettingsSection,
    },
    data() {
        return {
            isFormLoading: true,
            error: null,
            formErrors: null,
            scanErrors: {},
            description: "",
            token: "",
            shortlink: "",
            widgetLinkValue: null,
            qrTestLinkValue: null,
            widgetLinkUnavailableMessage: null,
            qrTestLinkUnavailableMessage: null,
            widgetImageFile: null,
            widgetImagePublicUri: null,
            collectCustomerName: false,
            collectBeeperNumber: false,
            collectCustomerComment: false,
            orderingHours: { weekly_windows: [], date_overrides: [] },
            orderingHoursTimezone: "",
            scanSettings: {
                qr_scan_invalidate_after_order: false,
                qr_scan_check_opening_hours: false,
                qr_scan_duration_enabled: false,
                qr_scan_duration_value: 60,
                qr_scan_duration_unit: "minutes",
            },
        };
    },
    computed: {
        rights() {
            return this.$store.getters["users/formattedRights"];
        },
        has_right_to_update() {
            return this.rights.includes("click_and_collect.config.update");
        },
        restaurant_id() {
            return Number.parseInt(this.$route.params.restaurant_id);
        },
        widgetActions() {
            return [
                { label: "Voir mon widget", icon: "fa-eye", url: this.widgetLinkValue, message: this.widgetLinkUnavailableMessage, testId: "widget-preview-link",
                    description: "Aperçu libre : commandes et paiements simulés, sans appliquer les règles QR." },
                { label: "Tester un QR code", icon: "fa-qrcode", url: this.qrTestLinkValue, message: this.qrTestLinkUnavailableMessage, testId: "qr-test-link",
                    description: "Parcours réel : règles QR appliquées. Les commandes et paiements sont réels." },
            ];
        },
    },
    created() {
        this.httpGet(`/api/ordering-table/${this.restaurant_id}/settings`).then((response) => {
            if (response && response.data && response.data.data) {
                this.widgetImagePublicUri = response.data.data.img;
                this.description = response.data.data.description || "";
                this.collectCustomerName = Boolean(response.data.data.collect_customer_name);
                this.collectBeeperNumber = Boolean(response.data.data.collect_beeper_number);
                this.collectCustomerComment = Boolean(response.data.data.collect_customer_comment);
                this.orderingHours = response.data.data.ordering_hours || this.orderingHours;
                this.orderingHoursTimezone = response.data.data.ordering_hours_timezone || "";
                for (const key of Object.keys(this.scanSettings)) {
                    this.scanSettings[key] = response.data.data[key] ?? this.scanSettings[key];
                }
            } else {
                this.error = "Impossible de charger les réglages. Recharge la page pour réessayer.";
            }
        }).catch(() => {
            this.error = "Impossible de charger les réglages. Recharge la page pour réessayer.";
        }).finally(() => { this.isFormLoading = false; });
        this.httpGet(`/api/ordering-table/${this.restaurant_id}/widget-url`, { handleReject: false }).then((response) => {
            if (response === false) {
                this.widgetLinkUnavailableMessage = "Aucune URL widget disponible pour cet établissement.";
                return;
            }

            if (response?.data?.data?.mode === "preview" && response.data.data.url) {
                this.widgetLinkValue = response.data.data.url;
                this.widgetLinkUnavailableMessage = null;
                return;
            }

            this.widgetLinkValue = null;
            this.widgetLinkUnavailableMessage = response?.data?.data?.message || "Aucune URL widget disponible pour cet établissement.";
        }).catch(() => {
            this.widgetLinkUnavailableMessage = "Impossible de charger l’aperçu. Recharge la page pour réessayer.";
        });
        this.httpGet(`/api/ordering-table/${this.restaurant_id}/qr-test-url`, { handleReject: false }).then((response) => {
            if (response?.data?.data?.mode === "qr" && response.data.data.url) {
                this.qrTestLinkValue = response.data.data.url;
                this.qrTestLinkUnavailableMessage = null;
                return;
            }
            this.qrTestLinkValue = null;
            this.qrTestLinkUnavailableMessage = response?.data?.data?.message || "Test QR indisponible. Recharge la page pour réessayer.";
        }).catch(() => {
            this.qrTestLinkUnavailableMessage = "Impossible de charger le test QR. Recharge la page pour réessayer.";
        });
    },
    methods: {
        onWidgetImageSelected(file) {
            this.widgetImageFile = file;
        },
        saveSettings() {
            if (this.isFormLoading) return;
            this.formErrors = null;
            this.scanErrors = {};
            const duration = this.scanSettings.qr_scan_duration_value;
            const maximum = this.scanSettings.qr_scan_duration_unit === "hours" ? 168 : 10080;
            if (this.scanSettings.qr_scan_duration_enabled && (!Number.isInteger(duration) || duration < 1 || duration > maximum)) {
                this.showErrors({ qr_scan_duration_value: ["Indique une durée entière entre 1 et 10 080 minutes, ou entre 1 et 168 heures."] });
                return;
            }
            if (this.scanSettings.qr_scan_check_opening_hours && !this.orderingHours.weekly_windows.length && !this.orderingHours.date_overrides.some((override) => override.windows.length)) {
                this.showErrors({ ordering_hours: ["Ajoute au moins un créneau de commande avant d’activer le contrôle des horaires."] });
                return;
            }
            this.isFormLoading = true;
            const data = new FormData();
            data.append("description", this.description);
            data.append("img", this.widgetImageFile);
            data.append("collect_customer_name", this.collectCustomerName ? "1" : "0");
            data.append("collect_beeper_number", this.collectBeeperNumber ? "1" : "0");
            data.append("collect_customer_comment", this.collectCustomerComment ? "1" : "0");
            if (this.scanSettings.qr_scan_check_opening_hours) {
                data.append("ordering_hours", JSON.stringify(this.orderingHours));
            }
            for (const [key, value] of Object.entries(this.scanSettings)) {
                if (!this.scanSettings.qr_scan_duration_enabled && ["qr_scan_duration_value", "qr_scan_duration_unit"].includes(key)) continue;
                data.append(key, Array.isArray(value) ? JSON.stringify(value) : typeof value === "boolean" ? (value ? "1" : "0") : String(value));
            }

            this.httpPost(`/api/ordering-table/${this.restaurant_id}/settings`, data, { handleReject: false })
                .catch((error) => {
                    this.scanErrors = error?.response?.data?.errors || {};
                    this.formErrors = Object.values(this.scanErrors).flat();
                    if (!this.formErrors.length) this.formErrors = ["Les réglages n’ont pas pu être enregistrés."];
                })
                .finally(() => {
                    this.isFormLoading = false;
                    if (this.formErrors) this.focusError();
                });
        },
        showErrors(errors) {
            this.scanErrors = errors;
            this.formErrors = Object.values(errors).flat();
            this.focusError();
        },
        focusError() {
            this.$nextTick(() => {
                const field = this.$el.querySelector('[aria-invalid="true"]');
                (field || this.$refs.formErrorSummary)?.focus();
            });
        },
    },
};
</script>

<style scoped>
.widget-actions { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1rem; margin: .5rem 0 1.25rem; }
.widget-action { padding: 1.25rem; border: 1px solid #e3eceb; border-radius: 16px; background: #fff; }
.widget-action .btn { min-height: 44px; display: inline-flex; align-items: center; padding-inline: 1.25rem; }
.widget-action p { margin: .75rem 0 0; color: #52676a; font-size: .875rem; line-height: 1.5; }
.widget-action .widget-action__error { color: #962d2d; }
.widget-action .btn:focus-visible { outline: 2px solid var(--main-color-light); outline-offset: 3px; }
.ordering-configuration-form { display: flex; flex-direction: column; gap: 1.75rem; margin: 1rem .75rem 0; }
.configuration-presentation { display: grid; grid-template-columns: minmax(180px, 1fr) minmax(0, 2fr); gap: 1.5rem; }
.configuration-presentation > div { min-width: 0; }
.configuration-setting { display: flex; justify-content: space-between; align-items: center; gap: 1.5rem; padding: 1rem 0; }
.configuration-setting + .configuration-setting { border-top: 1px solid #e9ecef; }
.configuration-setting:first-child { padding-top: 0; }
.configuration-setting:last-child { padding-bottom: 0; }
.configuration-setting > .lead-switch { flex-shrink: 0; }
.configuration-actions { display: flex; justify-content: flex-end; padding-top: 1.25rem; border-top: 1px solid var(--bs-border-color, #dee2e6); }
.configuration-actions .btn { min-height: 44px; padding-inline: 1.5rem; }
.configuration-actions .btn:focus-visible { outline: 2px solid var(--main-color-light); outline-offset: 3px; }
.qr-settings-errors { margin: 1rem; padding: 1rem; border-radius: 10px; background: #fff2f2; border: 1px solid #e8b6b6; color: #962d2d; }
.qr-settings-errors p:last-child { margin-bottom: 0; }
@media (max-width: 767px) {
    .widget-actions { grid-template-columns: minmax(0, 1fr); }
    .configuration-presentation { grid-template-columns: minmax(0, 1fr); }
}
</style>
